import React, { useEffect, useState, createContext, useContext } from 'react';
type Language = 'en' | 'ar';
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  isRTL: boolean;
}
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
// Translation dictionary
const translations: Record<Language, Record<string, string>> = {
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.exhibitors': 'Exhibitors',
    'nav.floorplan': 'Floorplan',
    'nav.agenda': 'Agenda',
    'nav.whatson': "What's On",
    'nav.schedule': 'My Schedule',
    'nav.alerts': 'Notifications',
    'nav.networking': 'Networking',
    'nav.buyers': 'Buyers',
    'nav.settings': 'Settings',
    'nav.menu': 'Menu',
    // Common
    'common.back': 'Back',
    'common.close': 'Close',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.view': 'View',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.all': 'All',
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    // Home Page
    'home.welcomeBack': 'Welcome Back!',
    'home.exploreHighlights': "Explore today's highlights",
    'home.floorplan': 'Floorplan',
    'home.agenda': 'Agenda',
    'home.schedule': 'Schedule',
    'home.alerts': 'Alerts',
    'home.notifications': 'Notifications',
    'home.sponsored': 'SPONSORED',
    'home.shopNow': 'Shop Now',
    'home.latestUpdates': 'Latest Updates',
    'home.trendingNow': 'Trending Now',
    'home.featuredEvent': 'Featured Event',
    'home.videoRecap': 'Video Recap',
    'home.watchNow': 'Watch Now',
    'home.getDirections': 'Get Directions',
    'home.reserveSeat': 'Reserve Seat',
    'home.joinSession': 'Join Session',
    'home.bookNow': 'Book Now',
    'home.viewSchedule': 'View Schedule',
    'home.viewAgenda': 'View Agenda',
    'home.registerNow': 'Register Now',
    'home.joinClass': 'Join Class',
    // Schedule
    'schedule.title': 'My Schedule',
    'schedule.sessions': 'sessions',
    'schedule.scheduled': 'scheduled',
    'schedule.items': 'items',
    'schedule.item': 'item',
    'schedule.noItems': 'No Items Scheduled',
    'schedule.bookSessions': 'Book sessions or schedule meetings to see them here',
    'schedule.conflictDetected': 'Schedule Conflict Detected',
    'schedule.overlappingItems': 'You have overlapping items in your schedule',
    // Agenda
    'agenda.title': 'Agenda',
    'agenda.available': 'available',
    'agenda.scheduled': 'scheduled',
    'agenda.allDays': 'All Days',
    'agenda.day': 'Day',
    'agenda.viewing': 'Viewing',
    'agenda.allSessions': 'All Sessions',
    'agenda.session': 'session',
    'agenda.sessions': 'sessions',
    // Alerts
    'alerts.title': 'Notifications',
    'alerts.subtitle': 'Manage your event notifications',
    'alerts.unread': 'unread',
    'alerts.needAction': 'need action',
    'alerts.markAllRead': 'Mark all read',
    'alerts.markRead': 'Mark read',
    'alerts.allCaughtUp': 'All caught up!',
    'alerts.noNotifications': 'No notifications',
    'alerts.readAll': "You've read all your notifications",
    'alerts.checkLater': 'Check back later for updates',
    'alerts.all': 'All',
    'alerts.unreadTab': 'Unread',
    'alerts.actionNeeded': 'Action Needed',
    // What's On
    'whatson.title': "What's On",
    'whatson.subtitle': 'Special events and experiences',
    'whatson.inviteOnly': 'Invite Only',
    'whatson.acceptInvitation': 'Accept Invitation',
    'whatson.decline': 'Decline',
    'whatson.attending': "You're attending this event",
    'whatson.declined': 'You declined this invitation',
    'whatson.noEvents': 'No Special Events',
    'whatson.checkLater': 'Check back later for exclusive events and experiences',
    'whatson.findOutMore': 'Find out more',
    // Settings
    'settings.title': 'Settings',
    'settings.subtitle': 'Manage your preferences',
    'settings.account': 'Account',
    'settings.language': 'Language',
    'settings.english': 'English',
    'settings.arabic': 'العربية',
    'settings.profile': 'My Profile',
    'settings.eventPreference': 'Event Preference',
    'settings.currentlyLabel': 'Currently',
    'settings.notifications': 'Notifications',
    'settings.dataStorage': 'Data & Storage',
    'settings.offlineData': 'Offline Data',
    'settings.lastSynced': 'Last synced',
    'settings.online': 'Online',
    'settings.offline': 'Offline',
    'settings.clearCache': 'Clear Cache',
    'settings.freeUp': 'Free up',
    'settings.privacySecurity': 'Privacy & Security',
    'settings.signOut': 'Sign Out',
    'settings.poweredBy': 'Powered by',
    // Networking
    'networking.title': 'Networking',
    'networking.subtitle': 'Connect with industry professionals',
    'networking.matches': 'Matches',
    'networking.attendees': 'Attendees',
    'networking.meetings': 'Meetings',
    'networking.chats': 'Chats',
    'networking.searchAttendees': 'Search attendees...',
    'networking.filters': 'Filters',
    'networking.active': 'Active',
    'networking.seniority': 'SENIORITY',
    'networking.allSeniority': 'All Seniority Levels',
    'networking.responsibility': 'RESPONSIBILITY',
    'networking.allResponsibilities': 'All Responsibilities',
    'networking.interests': 'INTERESTS',
    'networking.allInterests': 'All Interests',
    'networking.clearFilters': 'Clear All Filters',
    'networking.noAttendees': 'No attendees found',
    'networking.noMeetings': 'No meetings scheduled',
    'networking.noConversations': 'No conversations yet',
    'networking.startChatting': 'Start chatting with attendees to see your conversations here',
    'networking.meet': 'Meet',
    'networking.chat': 'Chat',
    'networking.view': 'View',
    'networking.match': 'Match',
    // Exhibitors
    'exhibitors.title': 'Exhibitors',
    'exhibitors.search': 'Search exhibitors...',
    'exhibitors.allCategories': 'All Categories',
    'exhibitors.viewStand': 'View Stand',
    'exhibitors.getDirections': 'Get Directions',
    'exhibitors.noExhibitors': 'No exhibitors found',
    'exhibitors.tryDifferent': 'Try a different search or filter',
    // Floor Plan
    'floorplan.title': 'Floor Plan',
    'floorplan.comingSoon': 'Interactive floor plan coming soon',
    'floorplan.meanwhile': 'Meanwhile, explore our exhibitors list',
    // Menu
    'menu.title': 'Menu',
    'menu.navigation': 'Navigation',
    'menu.switchEvent': 'Switch Event',
    'menu.connected': 'Connected',
    'menu.offline': 'Offline Mode',
    'menu.poweredBy': 'POWERED BY',
    'menu.syncStatus': 'Sync Status'
  },
  ar: {
    // Navigation
    'nav.home': 'الرئيسية',
    'nav.exhibitors': 'العارضون',
    'nav.floorplan': 'خريطة القاعة',
    'nav.agenda': 'الجدول',
    'nav.whatson': 'الفعاليات',
    'nav.schedule': 'جدولي',
    'nav.alerts': 'الإشعارات',
    'nav.networking': 'التواصل',
    'nav.buyers': 'المشترون',
    'nav.settings': 'الإعدادات',
    'nav.menu': 'القائمة',
    // Common
    'common.back': 'رجوع',
    'common.close': 'إغلاق',
    'common.save': 'حفظ',
    'common.cancel': 'إلغاء',
    'common.delete': 'حذف',
    'common.edit': 'تعديل',
    'common.view': 'عرض',
    'common.search': 'بحث',
    'common.filter': 'تصفية',
    'common.all': 'الكل',
    'common.loading': 'جاري التحميل...',
    'common.error': 'خطأ',
    'common.success': 'نجح',
    // Home Page
    'home.welcomeBack': 'مرحباً بعودتك!',
    'home.exploreHighlights': 'استكشف أبرز أحداث اليوم',
    'home.floorplan': 'خريطة القاعة',
    'home.agenda': 'الجدول',
    'home.schedule': 'جدولي',
    'home.alerts': 'التنبيهات',
    'home.notifications': 'الإشعارات',
    'home.sponsored': 'برعاية',
    'home.shopNow': 'تسوق الآن',
    'home.latestUpdates': 'آخر التحديثات',
    'home.trendingNow': 'الأكثر رواجاً',
    'home.featuredEvent': 'حدث مميز',
    'home.videoRecap': 'ملخص فيديو',
    'home.watchNow': 'شاهد الآن',
    'home.getDirections': 'احصل على الاتجاهات',
    'home.reserveSeat': 'احجز مقعد',
    'home.joinSession': 'انضم للجلسة',
    'home.bookNow': 'احجز الآن',
    'home.viewSchedule': 'عرض الجدول',
    'home.viewAgenda': 'عرض الأجندة',
    'home.registerNow': 'سجل الآن',
    'home.joinClass': 'انضم للفصل',
    // Schedule
    'schedule.title': 'جدولي',
    'schedule.sessions': 'جلسات',
    'schedule.scheduled': 'مجدولة',
    'schedule.items': 'عناصر',
    'schedule.item': 'عنصر',
    'schedule.noItems': 'لا توجد عناصر مجدولة',
    'schedule.bookSessions': 'احجز جلسات أو جدول اجتماعات لرؤيتها هنا',
    'schedule.conflictDetected': 'تم اكتشاف تعارض في الجدول',
    'schedule.overlappingItems': 'لديك عناصر متداخلة في جدولك',
    // Agenda
    'agenda.title': 'الجدول',
    'agenda.available': 'متاحة',
    'agenda.scheduled': 'مجدولة',
    'agenda.allDays': 'كل الأيام',
    'agenda.day': 'يوم',
    'agenda.viewing': 'عرض',
    'agenda.allSessions': 'كل الجلسات',
    'agenda.session': 'جلسة',
    'agenda.sessions': 'جلسات',
    // Alerts
    'alerts.title': 'الإشعارات',
    'alerts.subtitle': 'إدارة إشعارات الحدث',
    'alerts.unread': 'غير مقروءة',
    'alerts.needAction': 'تحتاج إجراء',
    'alerts.markAllRead': 'تحديد الكل كمقروء',
    'alerts.markRead': 'تحديد كمقروء',
    'alerts.allCaughtUp': 'لقد قرأت كل شيء!',
    'alerts.noNotifications': 'لا توجد إشعارات',
    'alerts.readAll': 'لقد قرأت جميع إشعاراتك',
    'alerts.checkLater': 'تحقق لاحقاً للحصول على التحديثات',
    'alerts.all': 'الكل',
    'alerts.unreadTab': 'غير مقروءة',
    'alerts.actionNeeded': 'مطلوب إجراء',
    // What's On
    'whatson.title': 'الفعاليات',
    'whatson.subtitle': 'فعاليات وتجارب خاصة',
    'whatson.inviteOnly': 'بدعوة فقط',
    'whatson.acceptInvitation': 'قبول الدعوة',
    'whatson.decline': 'رفض',
    'whatson.attending': 'أنت حاضر في هذا الحدث',
    'whatson.declined': 'لقد رفضت هذه الدعوة',
    'whatson.noEvents': 'لا توجد فعاليات خاصة',
    'whatson.checkLater': 'تحقق لاحقاً للحصول على فعاليات وتجارب حصرية',
    'whatson.findOutMore': 'اعرف المزيد',
    // Settings
    'settings.title': 'الإعدادات',
    'settings.subtitle': 'إدارة تفضيلاتك',
    'settings.account': 'الحساب',
    'settings.language': 'اللغة',
    'settings.english': 'English',
    'settings.arabic': 'العربية',
    'settings.profile': 'ملفي الشخصي',
    'settings.eventPreference': 'تفضيلات الحدث',
    'settings.currentlyLabel': 'حالياً',
    'settings.notifications': 'الإشعارات',
    'settings.dataStorage': 'البيانات والتخزين',
    'settings.offlineData': 'البيانات دون اتصال',
    'settings.lastSynced': 'آخر مزامنة',
    'settings.online': 'متصل',
    'settings.offline': 'غير متصل',
    'settings.clearCache': 'مسح ذاكرة التخزين المؤقت',
    'settings.freeUp': 'توفير',
    'settings.privacySecurity': 'الخصوصية والأمان',
    'settings.signOut': 'تسجيل الخروج',
    'settings.poweredBy': 'مدعوم من',
    // Networking
    'networking.title': 'التواصل',
    'networking.subtitle': 'تواصل مع المحترفين في الصناعة',
    'networking.matches': 'التطابقات',
    'networking.attendees': 'الحضور',
    'networking.meetings': 'الاجتماعات',
    'networking.chats': 'المحادثات',
    'networking.searchAttendees': 'البحث عن الحضور...',
    'networking.filters': 'التصفية',
    'networking.active': 'نشط',
    'networking.seniority': 'الأقدمية',
    'networking.allSeniority': 'جميع مستويات الأقدمية',
    'networking.responsibility': 'المسؤولية',
    'networking.allResponsibilities': 'جميع المسؤوليات',
    'networking.interests': 'الاهتمامات',
    'networking.allInterests': 'جميع الاهتمامات',
    'networking.clearFilters': 'مسح جميع الفلاتر',
    'networking.noAttendees': 'لم يتم العثور على حضور',
    'networking.noMeetings': 'لا توجد اجتماعات مجدولة',
    'networking.noConversations': 'لا توجد محادثات بعد',
    'networking.startChatting': 'ابدأ المحادثة مع الحضور لرؤية محادثاتك هنا',
    'networking.meet': 'اجتمع',
    'networking.chat': 'محادثة',
    'networking.view': 'عرض',
    'networking.match': 'تطابق',
    // Exhibitors
    'exhibitors.title': 'العارضون',
    'exhibitors.search': 'البحث عن العارضين...',
    'exhibitors.allCategories': 'جميع الفئات',
    'exhibitors.viewStand': 'عرض الجناح',
    'exhibitors.getDirections': 'احصل على الاتجاهات',
    'exhibitors.noExhibitors': 'لم يتم العثور على عارضين',
    'exhibitors.tryDifferent': 'جرب بحثاً أو تصفية مختلفة',
    // Floor Plan
    'floorplan.title': 'خريطة القاعة',
    'floorplan.comingSoon': 'خريطة القاعة التفاعلية قريباً',
    'floorplan.meanwhile': 'في هذه الأثناء، استكشف قائمة العارضين لدينا',
    // Menu
    'menu.title': 'القائمة',
    'menu.navigation': 'التنقل',
    'menu.switchEvent': 'تبديل الحدث',
    'menu.connected': 'متصل',
    'menu.offline': 'وضع عدم الاتصال',
    'menu.poweredBy': 'مدعوم من',
    'menu.syncStatus': 'حالة المزامنة'
  }
};
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('app-language');
    return saved as Language || 'en';
  });
  const isRTL = language === 'ar';
  useEffect(() => {
    localStorage.setItem('app-language', language);
    // Update document direction and lang attribute
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language, isRTL]);
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };
  const t = (key: string): string => {
    return translations[language][key] || key;
  };
  return <LanguageContext.Provider value={{
    language,
    setLanguage,
    t,
    isRTL
  }} data-id="element-4268">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}